import { type FunctionComponent, useState, useRef, useEffect } from 'react'

import classNames from 'classnames'
import { ChevronDown, ChevronUp } from 'lucide-react'

import { ContentSection } from '..'

interface CodyIdeProps {
    isLight?: boolean
}

export const CodyIde: FunctionComponent<CodyIdeProps> = ({ isLight = false }) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const [isAnimating, setIsAnimating] = useState(false)
    const contentRef = useRef<HTMLDivElement>(null)
    const [contentHeight, setContentHeight] = useState(0)

    const allIdes = [
        { name: 'VS Code', icon: 'vs_code.svg' },
        { name: 'IntelliJ', icon: 'IntelliJ_IDEA_icon.svg' },
        { name: 'PyCharm', icon: 'PyCharm_icon.svg' },
        { name: 'GoLand', icon: 'GoLand_icon.svg' },
        { name: 'RubyMine', icon: 'RubyMine_icon.svg' },
        { name: 'WebStorm', icon: 'WebStorm_icon.svg' },
        { name: 'PhpStorm', icon: 'PhpStorm_icon.svg' },
        { name: 'Rider', icon: 'Rider_icon.svg' },
        { name: 'CLion', icon: 'CLion_icon.svg' },
        { name: 'DataGrip', icon: 'DataGrip_icon.svg' },
        { name: 'DataSpell', icon: 'DataSpell_icon.svg' },
        { name: 'Aqua', icon: 'Aqua_icon.svg' },
        { name: 'RustRover', icon: 'RustRover_icon.svg' },
    ]

    const visibleIdes = isExpanded ? allIdes : allIdes.slice(0, 4)

    useEffect(() => {
        if (contentRef.current) {
            setContentHeight(contentRef.current.scrollHeight)
        }
    }, [isExpanded])

    // Handle the animation end for smoothness
    useEffect(() => {
        if (isAnimating) {
            const timeout = setTimeout(() => {
                setIsAnimating(false)
            }, 500) // Match the duration of the transition
            return () => clearTimeout(timeout)
        }
    }, [isAnimating])

    const handleToggle = (): void => {
        setIsAnimating(true)
        setIsExpanded(!isExpanded)
    }
    return (
        <ContentSection
            parentClassName="!py-0"
            className={classNames('flex w-full flex-col items-center md:flex-col', {
                'mb-[0] mt-16 gap-x-0 md:my-24 md:gap-x-12 md:pr-0': isLight,
                'mt-16 gap-x-12 gap-y-6 md:mt-14 md:pr-8 ': !isLight,
            })}
        >
            <h4
                className={classNames('mb-6 whitespace-nowrap', {
                    'text-gray-200': !isLight,
                    'text-gray-700': isLight,
                })}
            >
                Cody is available for:
            </h4>
            <div
                className={classNames('flex w-full flex-col items-center justify-center', {
                    'gap-x-6 gap-y-8 py-6': !isLight,
                    'md:gap-x-[47px]': isLight,
                })}
            >
                <div
                    // eslint-disable-next-line react/forbid-dom-props
                    style={{
                        maxHeight: isExpanded ? `${contentHeight}px` : '200px',
                        transition: 'max-height 0.5s ease',
                        overflow: 'hidden',
                    }}
                >
                    <div
                        ref={contentRef}
                        className={classNames('flex flex-wrap items-center justify-center gap-x-6', {
                            'w-full gap-y-8': !isLight,
                            'w-auto gap-y-4 md:gap-x-24': isLight,
                        })}
                    >
                        {visibleIdes.map(ide => (
                            <div
                                key={ide.name}
                                className={classNames('flex items-center gap-x-4', { 'py-3': isLight })}
                            >
                                <img
                                    className="h-[45px] w-[45px]"
                                    src={`/icons/IDEs/${ide.icon}`}
                                    alt={`${ide.name} IDE Marketplace`}
                                />
                                <h4
                                    className={classNames({
                                        'text-gray-200': !isLight,
                                        'text-[#0F111A]': isLight,
                                    })}
                                >
                                    {ide.name}
                                </h4>
                            </div>
                        ))}
                    </div>
                </div>

                <button
                    type="button"
                    onClick={handleToggle}
                    className={classNames('flex items-center  md:pb-0', {
                        'mt-4 pb-4 text-gray-200': !isLight,
                        'mt-6 text-gray-700 md:pb-4': isLight,
                    })}
                >
                    {isExpanded ? (
                        <>
                            Show less <ChevronUp size={20} className="ml-1" />
                        </>
                    ) : (
                        <>
                            Show more <ChevronDown size={20} className="ml-1" />
                        </>
                    )}
                </button>
            </div>
        </ContentSection>
    )
}
